import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useFetchDownload } from 'api/hooks'

import { RouteTicketingSystemsTicket } from 'routes'

import * as sessionUserSelector from 'selectors/sessionUser'

import { userTimeZoneFormat } from 'utils/time'
import { convertFileSize, capitalizeFirstLetter } from 'utils/strings'
import colors from 'components/utils/colors'

import IconLink from 'components/Shared/IconLink/Index'
import Tooltip from 'components/Shared/Tooltip/Index'
import Table from 'components/Shared/TableNew/Index'
import TableHead from 'components/Shared/TableNew/TableHead'
import TableBody from 'components/Shared/TableNew/TableBody'
import TableRow from 'components/Shared/TableNew/TableRow'
import TableCell from 'components/Shared/TableNew/TableCell'
import TableCellWithActions from 'components/Shared/TableNew/TableCellWithActions'
import TableRowNoData from 'components/Shared/TableNew/TableRowNoData'
import ImagePreview from './ImagePreview'
import CopyIcon from 'components/Shared/CopyIcon/Index'
import MarkedText from 'components/Shared/MarkedText/Index'

const FileName = styled.div`
  word-break: break-word;
  line-height: 18px;
`

const CreatedDate = styled.div`
  white-space: nowrap;
`

const CopyLinkIcon = styled(CopyIcon)`
  padding-right: 8px;
`

const AttachmentsTable = ({
  ticket, attachments, anchorElementId, searchValue,
}) => {
  const fetchDownload = useFetchDownload()
  const { t } = useTranslation()
  const userTimeZone = useSelector(sessionUserSelector.timezone)
  const isUserPrivileged = useSelector(sessionUserSelector.isUserPrivileged)

  const buildAttachmentLink = (attachmentId) => {
    const ticketUrl = RouteTicketingSystemsTicket({
      ticketingSystemDisplayId: ticket.ticketingSystemDisplayId,
      ticketId: ticket.externalId,
    })

    return `${window.location.origin}${ticketUrl}#attachments-${attachmentId}`
  }

  const onDownloadClick = (event, attachmentUrl) => {
    event.preventDefault()
    fetchDownload(attachmentUrl)
  }

  return (
    <Table width='100%' maxWidth='1112px'>
      <TableHead>
        <TableRow>
          {isUserPrivileged && <TableCell>{t('ticketPage.attachmentsHeaderId')}</TableCell>}
          <TableCell>{t('ticketPage.attachmentsHeaderFilename')}</TableCell>
          <TableCell>{t('ticketPage.attachmentsHeaderOwner')}</TableCell>
          <TableCell>{t('ticketPage.attachmentsHeaderCreated')}</TableCell>
          <TableCell>{t('ticketPage.attachmentsHeaderSize')}</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {attachments.map(attachment => {
          const anchorAttachmentId = `attachments-${attachment.id}`
          const isActiveRow = Boolean(anchorElementId === anchorAttachmentId)

          return (
            <TableRow
              key={attachment.id}
              id={anchorAttachmentId}
              active={isActiveRow}
            >
              {isUserPrivileged && <TableCell>{attachment.id}</TableCell>}
              <TableCell>
                <FileName>
                  <MarkedText text={attachment.file} markedPart={searchValue} />
                </FileName>
              </TableCell>
              <TableCell>{capitalizeFirstLetter(attachment.owner)}</TableCell>
              <TableCell>
                <CreatedDate>{userTimeZoneFormat(userTimeZone, attachment.created)}</CreatedDate>
              </TableCell>
              <TableCell>{convertFileSize(attachment.size)}</TableCell>
              <TableCellWithActions>
                <CopyLinkIcon
                  copyText={buildAttachmentLink(attachment.id)}
                  icon='icon-status-bonded'
                  tooltipPlace='top'
                  tooltipText={t('common.tooltipCopyLink')}
                  color={colors.secondary70}
                />
                <ImagePreview
                  imagePreviewUrl={attachment.previewUrl}
                />
                <a href={attachment.url} onClick={(e) => onDownloadClick(e, attachment.url)}>
                  <IconLink
                    iconName='icon-download-16'
                    color={colors.secondary70}
                    data-tooltip-content={t('common.tooltipDownload')}
                    data-tooltip-id={'attachment-tooltip-' + attachment.id}
                  />
                </a>
                <Tooltip id={'attachment-tooltip-' + attachment.id} />
              </TableCellWithActions>
            </TableRow>
          )
        })}
        {!attachments.length && (
          <TableRowNoData withTopBorder />
        )}
      </TableBody>
    </Table>
  )
}

AttachmentsTable.propTypes = {
  ticket: PropTypes.object,
  attachments: PropTypes.array,
  anchorElementId: PropTypes.string,
  searchValue: PropTypes.string,
}

export default React.memo(AttachmentsTable)
